import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Star } from 'lucide-react';
import gsap from 'gsap';
import { getPullData, getPullsData } from '../logic/pullLogic';
import { PLAYER } from '../data';

function Pull({ app, characterIds = [], bannerId }) {
  const [currentPullIndex, setCurrentPullIndex] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const characterRef = useRef(null);
  const infoRef = useRef(null);
  const overlayRef = useRef(null);
  const whiteOverlayRef = useRef(null);
  const starsContainerRef = useRef(null);
  const gradientOverlayRef = useRef(null);
  const backgroundRef = useRef(null);
  const summaryRef = useRef(null);
  const timelineRef = useRef(null);
  
  if (!characterIds.length) {
    throw new Error("character ids is empty");
  }

  const currentId = characterIds[Math.min(currentPullIndex, characterIds.length - 1)];
  const pullData = getPullData(currentId);
  const allPullsData = getPullsData(characterIds);
  const characterData = PLAYER.characters.find(c => c.prototypeId === currentId);
  const isNew = characterData?.pulls === 1;

  const showNextPull = () => {
    if (timelineRef.current) {
      timelineRef.current.kill();
    }
    if (currentPullIndex < characterIds.length - 1) {
      setCurrentPullIndex(prev => prev + 1);
    } else {
      setShowSummary(true);
    }
  };

  useEffect(() => {
    const starsContainer = starsContainerRef.current;
    if (starsContainer) {
      for (let i = 0; i < 50; i++) {
        const star = document.createElement('div');
        star.className = 'absolute w-0.5 h-12 bg-white/30 rotate-[15deg]';
        star.style.left = `${Math.random() * 100}%`;
        star.style.top = `${Math.random() * 100}%`;
        starsContainer.appendChild(star);
      }
    }

    if (showSummary && summaryRef.current) {
      gsap.from(summaryRef.current.children, {
        scale: 0,
        opacity: 0,
        duration: 0.5,
        stagger: 0.05,
        ease: "back.out(1.4)"
      });
      return;
    }

    if (timelineRef.current) {
      timelineRef.current.kill();
    }

    timelineRef.current = gsap.timeline();
    const tl = timelineRef.current;

    tl.set(overlayRef.current, {
      opacity: 0
    })
    .set(gradientOverlayRef.current, {
      opacity: 0
    })
    .set(starsContainer.children, {
      y: -100,
      opacity: 0
    })
    .set(backgroundRef.current, {
      background: 'linear-gradient(to bottom right, #ffffff, #e6e6ff)'
    });

    tl.to(whiteOverlayRef.current, {
      opacity: 0,
      duration: 0.1,
      ease: "power2.out"
    })
    .to(backgroundRef.current, {
      background: 'linear-gradient(to bottom right, #1a1a2e, #16213e, #1a1a2e)',
      duration: 0.5,
      ease: "power2.inOut"
    })
    .from(characterRef.current, {
      filter: 'brightness(0)',
      scale: 1.5,
      opacity: 0,
      duration: 0.5,
      ease: "power2.out"
    }, "<")
    .to([characterRef.current, gradientOverlayRef.current], {
      scale: 1,
      opacity: 1,
      duration: 0.5,
      ease: "power2.inOut"
    })
    .from(infoRef.current.children, {
      y: 50,
      opacity: 0,
      duration: 0.5,
      stagger: 0.1,
      ease: "back.out(1.4)"
    }, "<");

    return () => {
      if (starsContainer) {
        starsContainer.innerHTML = '';
      }
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
    };
  }, [currentPullIndex, showSummary]);

  if (showSummary) {
    return (
      <div className="h-full w-full mx-auto relative overflow-hidden text-white bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#1a1a2e]">
        <div className="p-8 flex flex-col h-full">
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Norse' }}>Pull Summary</h2>
          <div className="grid grid-cols-5 gap-4 mb-8" ref={summaryRef}>
            {allPullsData.map((pull, index) => (
              <div key={index} className="bg-black/30 p-2 rounded-lg">
                {pull.image ? (
                  <img 
                    src={pull.image}
                    alt={pull.name}
                    className="w-full h-24 object-cover rounded mb-2"
                  />
                ) : (
                  <div className="w-full h-24 flex items-center justify-center text-4xl">
                    {pull.icon}
                  </div>
                )}
                <div className="flex gap-0.5 mb-1">
                  {[...Array(pull.stars)].map((_, i) => (
                    <Star 
                      key={i} 
                      size={12} 
                      fill="currentColor" 
                      className="text-yellow-400" 
                    />
                  ))}
                </div>
                <p className="text-xs truncate">{pull.name}</p>
                {pull.type === 'character' && (
                  <p className="text-xs text-gray-400">
                    Pulls: {characterData?.pulls}
                  </p>
                )}
                {isNew && pull.type === 'character' && (
                  <span className="text-xs text-green-400">NEW!</span>
                )}
              </div>
            ))}
          </div>
          <div className="flex-1 flex items-center justify-center">
            <button
              onClick={() => app.goToBanners(bannerId)}
              className="px-8 py-3 text-[#a3e4f6] hover:text-white transition-colors text-xl"
              style={{ fontFamily: 'Futhark' }}
            >
              <span>
                Continue <span className="diamond-flash">◆</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={backgroundRef} 
      className="h-full w-full mx-auto relative overflow-hidden text-white transition-colors duration-500"
      onClick={showNextPull}
    >
      <style>
        {`
          @keyframes shine {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(100%);
            }
          }
          @keyframes flash {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }
          .diamond-flash {
            animation: flash 1s infinite;
          }
        `}
      </style>
      <button
        onClick={() => setShowSummary(true)}
        className="absolute top-6 right-6 z-50 px-4 py-2 bg-black/30 backdrop-blur-sm rounded-lg text-white/70 hover:text-white transition-colors"
      >
        Skip
      </button>
      <div ref={whiteOverlayRef} className="absolute inset-0 bg-white z-40" />
      <div ref={overlayRef} className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-500/20 via-purple-500/20 to-transparent z-10" />
      <div ref={gradientOverlayRef} className="absolute inset-0 bg-gradient-to-t from-blue-500/30 to-transparent z-20" />
      <div ref={starsContainerRef} className="absolute inset-0 overflow-hidden z-15" />
      
      <div className="relative h-full flex flex-col items-center justify-center">
        <div 
          ref={characterRef}
          className="absolute inset-0 flex items-center justify-center z-20"
        >
          {pullData.image ? (
            <img
              src={pullData.image}
              alt={pullData.name}
              className="h-[800px] object-contain mix-blend-screen"
            />
          ) : (
            <div className="text-[200px]">
              {pullData.icon}
            </div>
          )}
        </div>

        <div 
          ref={infoRef}
          className="absolute left-8 bottom-32 z-30"
        >
          <div className="bg-black/50 backdrop-blur-sm px-4 py-2 rounded-lg mb-2">
            <h2 
              className="text-3xl font-bold text-white drop-shadow-[0_0_10px_rgba(163,228,246,0.5)]" 
              style={{ fontFamily: 'Norse' }}
            >
              {pullData.name}
              {isNew && pullData.type === 'character' && (
                <span className="ml-2 text-sm bg-green-500 text-white px-2 py-0.5 rounded">NEW!</span>
              )}
            </h2>
            {pullData.type === 'character' && (
              <p className="text-sm text-gray-300">
                Total Pulls: {characterData?.pulls}
              </p>
            )}
          </div>
          <div className="flex gap-1 mb-2">
            {[...Array(pullData.stars)].map((_, i) => (
              <Star 
                key={i} 
                size={24} 
                fill="currentColor" 
                className="text-yellow-400 drop-shadow-[0_0_5px_rgba(255,215,0,0.5)]" 
              />
            ))}
          </div>
          <p 
            className="text-lg text-blue-200"
            style={{ fontFamily: 'Space Grotesk' }}
          >
            {pullData.subtitle}
          </p>
        </div>

        <div className="absolute bottom-6 right-6 z-40 flex items-center gap-2">
          <span className="text-[#a3e4f6]">
            {currentPullIndex + 1} / {characterIds.length}
          </span>
          <button
            onClick={showNextPull}
            className="px-6 py-2 text-[#a3e4f6] hover:text-white transition-colors"
            style={{ fontFamily: 'Futhark' }}
          >
            <span>
              Next <span className="diamond-flash">◆</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default Pull;
